import { Component } from '@theme/component';

// NH analytics — publish to Shopify Customer Events sandbox + dataLayer.
// Mirrors the publishEvent() helper in high-intent-visit-tracking.js.
function nhPublish(eventName, payload) {
  if (
    window.Shopify &&
    window.Shopify.analytics &&
    typeof window.Shopify.analytics.publish === 'function'
  ) {
    window.Shopify.analytics.publish(eventName, payload);
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...payload });
}

class ContactHub extends Component {
  #activeCategory = '';
  #refNumber = '';

  connectedCallback() {
    super.connectedCallback();

    this.#refNumber = this.#createRef();

    // Check if we're returning from a successful submission
    const params = new URLSearchParams(window.location.search);
    if (params.get('posted') === 'true') {
      this.#showThankYou(params.get('ref') || '');
      return;
    }

    this.#initTabs();
    this.#initAccordions();
    this.#initForms();
    this.#initCallbackDialog();
    this.#initChatButtons();
    this.#initChannelTracking();
  }

  /* ── CHANNEL TRACKING ── */

  // Publishes a `concierge_contact` analytics event whenever a visitor clicks
  // any of the four contact-hub channels (email / chat / phone / store).
  // High-intent signal — visitors reaching out are far more likely to convert.
  // Delegated listener so it covers both render states (form + thank-you).
  #initChannelTracking() {
    this.addEventListener('click', (e) => {
      const item = e.target.closest('.ch-channels__item');
      if (!item || !this.contains(item)) return;

      let channel = 'unknown';
      if (item.matches('a[href^="mailto:"]')) {
        channel = 'email';
      } else if (item.matches('a[href*="/pages/stores"]')) {
        channel = 'store';
      } else if (item.getAttribute('aria-haspopup') === 'dialog') {
        channel = 'phone';
      } else if (item.tagName === 'BUTTON') {
        // Remaining button = chat (Front widget or fallback)
        channel = 'chat';
      }

      nhPublish('concierge_contact', {
        channel,
        page_path: window.location.pathname,
      });
    });
  }

  /* ── CHAT BUTTON ── */

  // WCAG 4.1.2 — replaces the inline onclick that used to live on the chat
  // button. If FrontChat is loaded we open the chat; otherwise we fall back
  // to /pages/contact so the control always does something meaningful for
  // keyboard and screen-reader users. Two instances of contact-hub-channels
  // render in the DOM (thank-you state + form state), so ref is an array.
  #initChatButtons() {
    const buttons = this.refs.chatButton;
    if (!buttons) return;
    const list = Array.isArray(buttons) ? buttons : [buttons];
    for (const btn of list) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof window.FrontChat === 'function') {
          window.FrontChat('show');
        } else {
          window.location.href = (window.NH_ROUTES && window.NH_ROUTES.contact) || '/pages/contact';
        }
      });
    }
  }

  /* ── TABS ── */

  #initTabs() {
    const tabs = this.refs.tabs;
    if (!tabs || !tabs.length) return;

    for (const tab of tabs) {
      tab.addEventListener('click', (e) => this.handleTabClick(e));
      tab.addEventListener('keydown', (e) => this.handleTabKeydown(e));
    }

    const activeTab = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    if (activeTab) {
      this.#activateTab(activeTab, false);
    } else if (tabs[0]) {
      this.#activateTab(tabs[0], false);
    }
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    if (tab.getAttribute('aria-selected') === 'true') return;
    this.#activateTab(tab, true);
  }

  handleTabKeydown(event) {
    const tabs = this.refs.tabs;
    if (!tabs || !tabs.length) return;

    const current = tabs.indexOf(event.currentTarget);
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        next = 0;
        break;
      case 'End':
        event.preventDefault();
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    if (next >= 0) {
      this.#activateTab(tabs[next], true);
      tabs[next].focus();
    }
  }

  #activateTab(tab, animate) {
    const tabs = this.refs.tabs;
    const panels = this.refs.panels;
    if (!tabs || !panels) return;

    const targetId = tab.getAttribute('aria-controls');
    this.#activeCategory = tab.dataset.category || '';

    for (const t of tabs) {
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    }
    for (const p of panels) {
      p.setAttribute('hidden', '');
      p.classList.remove('ch-panel--active');
    }

    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');

    const panel = panels.find((p) => p.id === targetId);
    if (panel) {
      panel.removeAttribute('hidden');
      if (animate) void panel.offsetHeight;
      panel.classList.add('ch-panel--active');
      if (animate) panel.focus({ preventScroll: true });
    }

    this.#updateInquiryType();
    this.#updateConditionalFields();
    this.#closeAllAccordions();
  }

  #updateInquiryType() {
    const field = this.refs.inquiryType;
    if (field) field.value = this.#activeCategory;
  }

  // WCAG 4.1.2 invariant: the group's `hidden` attribute and the nested
  // input's `disabled` attribute MUST always be toggled together. If we
  // ever remove `hidden` without also removing `disabled`, the user sees
  // a field they are physically prevented from typing into. If we ever
  // remove `disabled` without also removing `hidden`, a disabled value
  // would silently accompany the form submission. The paired toggle
  // below is the single source of truth — do not split it across two
  // flows or add a second code path that only touches one attribute.
  #updateConditionalFields() {
    const groups = this.refs.conditionalFields;
    if (!groups || !groups.length) return;

    for (const group of groups) {
      const allowed = (group.dataset.categories || '').split(',').map((s) => s.trim());
      const input = group.querySelector('input, textarea');
      const shouldShow = allowed.includes(this.#activeCategory);

      if (shouldShow) {
        group.removeAttribute('hidden');
        if (input) input.removeAttribute('disabled');
      } else {
        group.setAttribute('hidden', '');
        if (input) {
          input.setAttribute('disabled', '');
          input.value = '';
        }
      }
    }
  }

  /* ── ACCORDION ── */

  #initAccordions() {
    const summaries = this.querySelectorAll('.ch-faq__question');
    for (const summary of summaries) {
      summary.addEventListener('click', (e) => this.#handleAccordionToggle(e));
    }
  }

  #handleAccordionToggle(event) {
    const summary = event.currentTarget;
    const details = summary.closest('details');
    if (!details) return;

    const panel = details.closest('[role="tabpanel"]');
    if (!panel) return;

    if (!details.open) {
      const siblings = panel.querySelectorAll('details[open]');
      for (const sib of siblings) {
        if (sib !== details) sib.removeAttribute('open');
      }
    }
  }

  #closeAllAccordions() {
    const openDetails = this.querySelectorAll('.ch-faq__item[open]');
    for (const d of openDetails) d.removeAttribute('open');
  }

  /* ── FORMS ── */

  #initForms() {
    const ref = this.#refNumber;

    // Set reference number on all forms
    const refFields = this.querySelectorAll('input[name="contact[Reference]"]');
    for (const field of refFields) {
      field.value = ref;
    }

    // Build the return URL (current page + posted flag + ref)
    const currentPath = window.location.pathname;
    const returnUrl = currentPath + '?posted=true&ref=' + encodeURIComponent(ref);

    // Set return_to on all forms
    const returnFields = this.querySelectorAll('input[name="return_to"]');
    for (const field of returnFields) {
      field.value = returnUrl;
    }

    // Validation
    const forms = this.querySelectorAll('form');
    for (const form of forms) {
      form.addEventListener('submit', (e) => {
        const invalids = form.querySelectorAll(':invalid');
        // Find error container in this form's context. Callback dialog
        // wraps its form in `.ch-dialog__inner` (the error region is a
        // sibling of the form). Main form wraps its inputs in `.ch-form`
        // which is itself a descendant of the form, so we search
        // descendants directly. Fall back to closest() for backwards
        // compat with any future markup change.
        const errorEl = form.closest('.ch-dialog__inner')?.querySelector('.ch-dialog__error')
          || form.querySelector('.ch-form__errors')
          || form.closest('.ch-form')?.querySelector('.ch-form__errors');

        if (invalids.length > 0) {
          e.preventDefault();
          // WCAG 3.3.1 / 4.1.2 — pair the visual error styling with
          // aria-invalid="true" so screen readers announce field-level
          // invalidity, not just the page-level error region.
          for (const el of invalids) {
            el.classList.add('ch-input--error');
            el.setAttribute('aria-invalid', 'true');
          }
          if (errorEl) errorEl.removeAttribute('hidden');
          invalids[0].focus();
        } else {
          // Clear any prior aria-invalid before submission proceeds.
          for (const el of form.querySelectorAll('[aria-invalid="true"]')) {
            el.removeAttribute('aria-invalid');
          }
          if (errorEl) errorEl.setAttribute('hidden', '');

          // Bottom-of-funnel concierge signal — visitor actually submitted a
          // contact form (vs `concierge_contact` which fires on channel click).
          // Deduped per session so callback dialog + main form don't double-fire.
          try {
            if (sessionStorage.getItem('nh_concierge_engaged_fired') !== '1') {
              sessionStorage.setItem('nh_concierge_engaged_fired', '1');
              const isCallback = !!form.closest('.ch-dialog__inner');
              nhPublish('concierge_engaged', {
                category: this.#activeCategory || 'unknown',
                channel: isCallback ? 'callback' : 'form',
                page_path: window.location.pathname,
                ref: this.#refNumber,
              });
            }
          } catch (_) {
            // sessionStorage unavailable (private browsing) — fail silently
          }
        }
      });

      const allInputs = form.querySelectorAll('.ch-form__input, .ch-dialog__input, .ch-dialog__select');
      for (const input of allInputs) {
        // WCAG 3.3.1 — clear both visual + AT error state when the user
        // starts to correct the field. We listen on `input` (not just focus)
        // so re-validation tracks every keystroke, not just initial focus.
        const clearError = () => {
          input.classList.remove('ch-input--error');
          input.removeAttribute('aria-invalid');
        };
        input.addEventListener('focus', clearError);
        input.addEventListener('input', clearError);
      }
    }
  }

  #createRef() {
    const now = new Date();
    const date = now.toISOString().slice(2, 10).replace(/-/g, '');
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 4; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return 'NH-' + date + '-' + code;
  }

  /* ── THANK YOU STATE ── */

  #showThankYou(ref) {
    const formArea = this.refs.formArea;
    const thankyou = this.refs.thankyou;
    const refDisplay = this.refs.refDisplay;

    if (formArea) formArea.setAttribute('hidden', '');
    if (thankyou) thankyou.removeAttribute('hidden');
    if (refDisplay && ref) refDisplay.textContent = ref;

    // Clean the URL so refreshing doesn't re-show thank you
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'instant' });
    this.refs.thankyouHeading?.focus({ preventScroll: true });
  }

  /* ── CALLBACK DIALOG ── */

  #initCallbackDialog() {
    const openBtn = this.refs.callbackOpen;
    const dialog = this.refs.callbackDialog;
    const closeBtn = this.refs.callbackClose;

    if (!openBtn || !dialog) return;

    openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (dialog.open) return;
      // showModal() puts the dialog in the top layer and makes the rest of
      // the page inert on its own, so no attribute is set on the page here.
      dialog.showModal();
      // Move focus to first input inside dialog
      const firstInput = dialog.querySelector('input:not([type="hidden"]), select, textarea');
      if (firstInput) {
        requestAnimationFrame(() => firstInput.focus());
      }
    });

    // Every close path (button, backdrop, Escape, form or script) ends here,
    // so focus always returns to the button that opened the dialog.
    dialog.addEventListener('close', () => {
      if (openBtn.isConnected) openBtn.focus();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        dialog.close();
      });
    }

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        dialog.close();
      }
    });

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dialog.close();
      }
    });
  }
}

if (!customElements.get('nh-contact-hub')) {
  customElements.define('nh-contact-hub', ContactHub);
}